import { toastController } from '@ionic/vue';
import { ApiError } from '@/core/http/error';

export interface ToastOptions {
  id?: string;
  duration?: number;
}

type Tone = 'success' | 'danger' | 'warning' | 'info' | 'medium';

const live = new Set<string>();

async function present(message: string, tone: Tone, opts: ToastOptions = {}): Promise<void> {
  if (opts.id && live.has(opts.id)) return;
  if (opts.id) live.add(opts.id);

  const color =
    tone === 'success'
      ? 'success'
      : tone === 'danger'
        ? 'danger'
        : tone === 'warning'
          ? 'warning'
          : tone === 'info'
            ? 'primary'
            : 'medium';

  const toast = await toastController.create({
    message,
    duration: opts.duration ?? (tone === 'danger' ? 5000 : 3000),
    color,
    swipeGesture: 'vertical',
    position: 'top',
    buttons: [{ text: 'OK', role: 'cancel' }],
  });
  await toast.present();
  await toast.onDidDismiss();
  if (opts.id) live.delete(opts.id);
}

function withRequestId(msg: string, err: ApiError): string {
  if (!err.requestId) return msg;
  // Show the short prefix — full UUID is noisy in a toast but the first 8
  // chars are enough for ops to grep the backend log.
  return `${msg}\nRef: ${err.requestId.slice(0, 8)}`;
}

function formatApiError(err: ApiError): string {
  // 5xx from Laravel-with-debug leaks the raw exception message
  // (`Argument #1 ... must be of type int, string given`). That's useful in
  // the console but unusable in a toast — swap in a friendly line.
  if (err.status !== null && err.status >= 500) {
    // eslint-disable-next-line no-console -- 5xx debug channel; scrubbed by Sentry beforeBreadcrumb.
    console.error('[api]', err.status, err.requestId ?? '-', err.message);
    return withRequestId('Something went wrong on the server. Please try again.', err);
  }
  if (!err.errors) return withRequestId(err.message, err);
  const lines = Object.entries(err.errors)
    .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
    .join(' · ');
  return withRequestId(lines ? `${err.message} — ${lines}` : err.message, err);
}

export function useToast() {
  return {
    success: (message: string, opts?: ToastOptions) => present(message, 'success', opts),
    danger: (message: string, opts?: ToastOptions) => present(message, 'danger', opts),
    warning: (message: string, opts?: ToastOptions) => present(message, 'warning', opts),
    info: (message: string, opts?: ToastOptions) => present(message, 'info', opts),
    /** Present a danger toast with all Laravel-style field errors expanded. */
    apiError: (err: unknown, fallback = 'Something went wrong.', opts?: ToastOptions) => {
      if (err instanceof ApiError) return present(formatApiError(err), 'danger', opts);
      return present(fallback, 'danger', opts);
    },
  };
}
